// Vista de la evidencia automática de un ticket (detection_signal.evidencia).
// Orden pensado para quien lo atiende: primero lo que la persona DIJO (literal),
// luego dónde estaba, lo que el agente entendió, lo que se vio/oyó, lo que
// falló, y al final la conversación completa (plegada). Solo tokens semánticos.

import { useState, type ReactNode } from 'react';
import {
  Bot,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  FileText,
  Film,
  Image as ImageIcon,
  Link2,
  ListOrdered,
  Paperclip,
  Monitor,
  Music,
  Quote,
  ShieldCheck,
  Smartphone,
  User,
  Wrench,
  XCircle,
} from 'lucide-react';

import {
  CANAL_LABEL,
  DISPOSITIVO_LABEL,
  MODO_LABEL,
  tieneContenido,
  type Evidencia,
  type Herramienta,
  type Medio,
  type Turno,
} from './evidencia';

const fmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
function when(s?: string): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : fmt.format(d);
}

function Section({ icon, title, count, children }: { icon: ReactNode; title: string; count?: number; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-muted/30 p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary" aria-hidden>{icon}</span>
        <span className="min-w-0 flex-1">{title}</span>
        {count !== undefined && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">{count}</span>}
      </h3>
      {children}
    </section>
  );
}

function Chip({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'primary' | 'ok' | 'error' }) {
  const t = {
    muted: 'bg-muted text-muted-foreground',
    primary: 'bg-primary/10 text-primary',
    ok: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    error: 'bg-destructive/10 text-destructive',
  }[tone];
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${t}`}>{children}</span>;
}

function Quien({ de }: { de?: string }) {
  if (de === 'user') return <Chip><User size={11} aria-hidden /> Persona</Chip>;
  if (de === 'assistant') return <Chip><Bot size={11} aria-hidden /> Agente</Chip>;
  return null;
}

// ── Medios ─────────────────────────────────────────────────────────────

const MEDIO_ICON: Record<Medio['tipo'], ReactNode> = {
  imagen: <ImageIcon size={14} aria-hidden />,
  video: <Film size={14} aria-hidden />,
  audio: <Music size={14} aria-hidden />,
  pdf: <FileText size={14} aria-hidden />,
  enlace: <Link2 size={14} aria-hidden />,
};
const MEDIO_LABEL: Record<Medio['tipo'], string> = { imagen: 'Imagen', video: 'Video', audio: 'Audio', pdf: 'PDF', enlace: 'Enlace' };

function nombreUrl(url: string): string {
  try {
    const u = new URL(url);
    const last = decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() || '');
    return last ? `${u.hostname} · ${last}` : u.hostname;
  } catch {
    return url;
  }
}

function MedioCard({ m }: { m: Medio }) {
  const [roto, setRoto] = useState(false);
  const abrir = (
    <a href={m.url} target="_blank" rel="noreferrer noopener" className="inline-flex min-w-0 items-center gap-1 text-xs font-medium text-primary hover:underline">
      <span className="truncate">{nombreUrl(m.url)}</span> <ExternalLink size={12} className="shrink-0" aria-hidden />
      <span className="sr-only">(se abre en otra pestaña)</span>
    </a>
  );
  let preview: ReactNode = null;
  if (!roto && m.tipo === 'imagen') {
    preview = (
      <a href={m.url} target="_blank" rel="noreferrer noopener" className="block overflow-hidden rounded-xl border border-border bg-card">
        <img src={m.url} alt="Imagen de la conversación" loading="lazy" onError={() => setRoto(true)} className="max-h-56 w-full object-contain" />
      </a>
    );
  } else if (!roto && m.tipo === 'video') {
    preview = (
      <video src={m.url} controls preload="metadata" playsInline onError={() => setRoto(true)} className="max-h-64 w-full rounded-xl border border-border bg-card">
        Tu navegador no puede reproducir este video.
      </video>
    );
  } else if (!roto && m.tipo === 'audio') {
    preview = <audio src={m.url} controls preload="none" onError={() => setRoto(true)} className="w-full" />;
  }
  return (
    <li className="flex min-w-0 flex-col gap-2 rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="primary">{MEDIO_ICON[m.tipo]} {MEDIO_LABEL[m.tipo]}</Chip>
        <Quien de={m.de} />
        {m.ts && <span className="text-[11px] text-muted-foreground">{when(m.ts)}</span>}
      </div>
      {preview}
      {roto && <p className="text-xs text-muted-foreground">No se pudo mostrar la vista previa (el enlace puede haber caducado).</p>}
      {abrir}
    </li>
  );
}

// ── Herramientas ───────────────────────────────────────────────────────

function HerramientaRow({ h }: { h: Herramienta }) {
  return (
    <li className="rounded-xl border border-border bg-card px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 break-all font-mono text-[12px] font-semibold text-foreground">{h.tool}</code>
        {h.ok === true && <Chip tone="ok"><CheckCircle2 size={11} aria-hidden /> Bien</Chip>}
        {h.ok === false && <Chip tone="error"><XCircle size={11} aria-hidden /> Falló</Chip>}
        {h.ok === null && <Chip>Sin resultado</Chip>}
        {h.ts && <span className="ml-auto text-[11px] text-muted-foreground">{when(h.ts)}</span>}
      </div>
      {h.error && <p className="mt-1.5 whitespace-pre-wrap break-words rounded-lg bg-destructive/10 px-2 py-1.5 font-mono text-[12px] text-destructive">{h.error}</p>}
      {h.resumen && <p className="mt-1.5 break-words text-xs text-muted-foreground">{h.resumen}</p>}
    </li>
  );
}

// ── Conversación ───────────────────────────────────────────────────────

function TurnoBurbuja({ t }: { t: Turno }) {
  const user = t.rol === 'user';
  return (
    <li className={`flex ${user ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[88%] rounded-2xl px-3 py-2 ${user ? 'rounded-br-md bg-primary/10' : 'rounded-bl-md border border-border bg-card'}`}>
        <p className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
          {user ? <User size={11} aria-hidden /> : <Bot size={11} aria-hidden />}
          {user ? 'Persona' : 'Agente'}
          {t.ts && <span className="font-normal">· {when(t.ts)}</span>}
        </p>
        <p className="whitespace-pre-wrap break-words text-sm text-foreground">{t.texto}</p>
      </div>
    </li>
  );
}

// ── Vista ──────────────────────────────────────────────────────────────

export function EvidenciaTicket({ evidencia, textoOriginal }: { evidencia: Evidencia | null; textoOriginal?: string }) {
  const [verUa, setVerUa] = useState(false);

  if (!tieneContenido(evidencia)) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-4">
        <p className="text-sm font-medium text-foreground">Este ticket no trae evidencia automática</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Se creó antes de que existiera o desde un lugar que no la adjunta. Lo que hay es la descripción
          {textoOriginal ? ' y el texto registrado al crearlo (puede ser una paráfrasis del agente).' : '.'}
        </p>
        {textoOriginal && (
          <blockquote className="mt-3 border-l-2 border-border pl-3 text-sm italic text-muted-foreground">{textoOriginal}</blockquote>
        )}
      </div>
    );
  }

  const e = evidencia;
  const p = e.pantalla;
  const a = e.agente;
  const DevIcon = p?.dispositivo === 'desktop' ? Monitor : Smartphone;

  return (
    <div className="flex flex-col gap-3">
      {/* Origen + link a la conversación */}
      <div className="flex flex-wrap items-center gap-2">
        {e.canal && <Chip tone="primary">{CANAL_LABEL[e.canal] ?? e.canal}</Chip>}
        {e.pii_enmascarada && <Chip><ShieldCheck size={11} aria-hidden /> Datos personales ocultos</Chip>}
        {e.conversation_url ? (
          <a
            href={e.conversation_url}
            target={e.conversation_url.startsWith('/') ? undefined : '_blank'}
            rel="noreferrer noopener"
            className="ml-auto inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            Abrir la conversación <ExternalLink size={12} aria-hidden />
          </a>
        ) : e.thread_id || e.session_id ? (
          <span className="ml-auto truncate font-mono text-[11px] text-muted-foreground" title="Conversación">{e.thread_id ? `hilo ${e.thread_id}` : `sesión ${e.session_id}`}</span>
        ) : null}
      </div>

      {/* 1. Palabras literales */}
      {e.palabras_del_usuario.length > 0 ? (
        <Section icon={<Quote size={14} />} title="Lo que dijo la persona (literal)">
          <ul className="flex flex-col gap-2">
            {e.palabras_del_usuario.map((w, i) => {
              const ultima = i === e.palabras_del_usuario.length - 1;
              return (
                <li key={i}>
                  <blockquote className={`whitespace-pre-wrap break-words border-l-2 pl-3 text-sm ${ultima ? 'border-primary font-medium text-foreground' : 'border-border text-muted-foreground'}`}>
                    «{w}»
                  </blockquote>
                </li>
              );
            })}
          </ul>
        </Section>
      ) : textoOriginal ? (
        <Section icon={<Quote size={14} />} title="Texto registrado">
          <blockquote className="border-l-2 border-border pl-3 text-sm italic text-muted-foreground">{textoOriginal}</blockquote>
        </Section>
      ) : null}

      {/* 2. Pantalla */}
      {p && (
        <Section icon={<DevIcon size={14} />} title="Dónde estaba">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {p.ruta && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-muted-foreground">Pantalla</dt>
                <dd className="mt-0.5 break-all font-mono text-[13px] text-foreground">{p.ruta}</dd>
              </div>
            )}
            {p.titulo && (
              <div className="sm:col-span-2">
                <dt className="text-xs text-muted-foreground">Título</dt>
                <dd className="mt-0.5 break-words text-foreground">{p.titulo}</dd>
              </div>
            )}
            {(p.dispositivo || p.modo) && (
              <div>
                <dt className="text-xs text-muted-foreground">Dispositivo</dt>
                <dd className="mt-0.5 text-foreground">
                  {[p.dispositivo ? DISPOSITIVO_LABEL[p.dispositivo] ?? p.dispositivo : null, p.modo ? MODO_LABEL[p.modo] ?? p.modo : null].filter(Boolean).join(' · ')}
                </dd>
              </div>
            )}
            {p.viewport && (
              <div>
                <dt className="text-xs text-muted-foreground">Tamaño de pantalla</dt>
                <dd className="mt-0.5 text-foreground">{p.viewport.replace('x', ' × ')} px</dd>
              </div>
            )}
            {p.version_app && (
              <div>
                <dt className="text-xs text-muted-foreground">Versión</dt>
                <dd className="mt-0.5 font-mono text-[13px] text-foreground">{p.version_app}</dd>
              </div>
            )}
          </dl>
          {p.user_agent && (
            <div className="mt-2">
              <button type="button" onClick={() => setVerUa((v) => !v)} aria-expanded={verUa} className="text-xs font-medium text-primary hover:underline">
                {verUa ? 'Ocultar navegador' : 'Ver navegador (user agent)'}
              </button>
              {verUa && <p className="mt-1 break-all font-mono text-[11px] text-muted-foreground">{p.user_agent}</p>}
            </div>
          )}
        </Section>
      )}

      {/* 3. Lo que el agente entendió */}
      {a && (
        <Section icon={<ListOrdered size={14} />} title="Lo que entendió el agente">
          {a.pasos.length > 0 && (
            <>
              <p className="mb-1 text-xs text-muted-foreground">Pasos</p>
              <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm text-foreground">
                {a.pasos.map((s, i) => <li key={i} className="break-words">{s}</li>)}
              </ol>
            </>
          )}
          {(a.esperado || a.actual) && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {a.esperado && (
                <div className="rounded-xl border border-border bg-card p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Esperaba</p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">{a.esperado}</p>
                </div>
              )}
              {a.actual && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-destructive">Pasó</p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">{a.actual}</p>
                </div>
              )}
            </div>
          )}
          {a.urls.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1">
              {a.urls.map((u) => (
                <li key={u}>
                  <a href={u} target="_blank" rel="noreferrer noopener" className="inline-flex max-w-full items-center gap-1 text-xs font-medium text-primary hover:underline">
                    <Link2 size={12} className="shrink-0" aria-hidden /> <span className="truncate">{nombreUrl(u)}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {/* 4. Medios */}
      {e.medios.length > 0 && (
        <Section icon={<ImageIcon size={14} />} title="Lo que se vio en la conversación" count={e.medios.length}>
          <ul className="grid grid-cols-1 items-start gap-2 sm:grid-cols-2">
            {e.medios.map((m) => <MedioCard key={m.url} m={m} />)}
          </ul>
        </Section>
      )}

      {/* 4b. Adjuntos del usuario (documentos subidos al chat) */}
      {e.adjuntos.length > 0 && (
        <Section icon={<Paperclip size={14} />} title="Archivos que adjuntó la persona" count={e.adjuntos.length}>
          <ul className="flex flex-col gap-1.5">
            {e.adjuntos.map((d) => (
              <li key={d.document_id} className="flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm">
                <FileText size={14} className="shrink-0 text-muted-foreground" aria-hidden />
                {d.url ? (
                  <a href={d.url} target="_blank" rel="noreferrer noopener" className="min-w-0 truncate font-medium text-primary hover:underline">{d.nombre || nombreUrl(d.url)}</a>
                ) : (
                  <span className="min-w-0 truncate text-foreground">{d.nombre || 'Documento'}</span>
                )}
                <code className="ml-auto break-all font-mono text-[11px] text-muted-foreground">{d.document_id}</code>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* 5. Herramientas */}
      {e.herramientas.length > 0 && (
        <Section icon={<Wrench size={14} />} title="Herramientas que usó el agente" count={e.herramientas.length}>
          <ul className="flex flex-col gap-2">
            {e.herramientas.map((h, i) => <HerramientaRow key={`${h.tool}-${i}`} h={h} />)}
          </ul>
        </Section>
      )}

      {/* 6. Conversación (plegada) */}
      {e.conversacion.length > 0 && (
        <details className="group rounded-2xl border border-border bg-muted/30 p-4">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary" aria-hidden><Bot size={14} /></span>
            <span className="min-w-0 flex-1">Conversación</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">{e.conversacion.length} mensajes</span>
            <ChevronDown size={16} className="text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden />
          </summary>
          <ul className="mt-3 flex flex-col gap-2">
            {e.conversacion.map((t, i) => <TurnoBurbuja key={i} t={t} />)}
          </ul>
        </details>
      )}
    </div>
  );
}

export default EvidenciaTicket;
